import { CourseTabTitle } from "../../CourseTabs/CourseTabTitle";
import { McAnswer } from "./MultipleChoiceStep";
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import _ from "lodash";
import { useForm } from "react-hook-form";
import { useTranslation } from "@vixi/localization";
import { CourseStepDivider, CourseStepPaddingWrapper, EditableBox } from "..";
import { CourseTabComponentProps } from "@vixi/types";
import { CourseData } from "@vixi/types";
import { useCourseEditorContext } from "../../context";
import useUiStore from "@/store/ui.state";
import { TransparentTextArea } from "@/components/input";
import { Button } from "@/components/base/button";

const fitbBlank = "____";

export interface FitbStepProps extends CourseTabComponentProps, CourseData.FitbQuestion {}
export interface FitbQuestionFormValues extends Omit<CourseData.FitbQuestion, "options"> {
  options: { value: string }[];
}

export function FitbStep(props: FitbStepProps) {
  const { t } = useTranslation();
  const path = useMemo(() => props.path.concat("fillInTheBlank"), [props.path]);
  const { mode } = useUiStore((state) => state.courseEditor);

  const [questionIsFocused, setQuestionIsFocused] = useState(false);
  const questionContainerRef = useRef<HTMLDivElement | null>(null);
  const questionTextAreaRef = useRef<HTMLTextAreaElement | null>(null);

  // Question form
  const fitbQuestionForm = useForm<FitbQuestionFormValues>({
    defaultValues: {
      question: props.question ?? "",
      options: props.options.map((option) => ({ value: option })),
      correctAnswer: props.correctAnswer,
    },
  });

  const { watch, register, getValues, setValue: setFieldValue } = fitbQuestionForm;

  const question = watch("question");
  const answers = watch("options");
  const correctAnswer = watch("correctAnswer");

  const hasBlank = question?.includes(fitbBlank);
  const showAddBlank = !hasBlank && questionIsFocused;

  // Handle blank detection and insertion
  const { ref: questionTextAreaRefCb, ...registerQuestion } = register("question");
  useImperativeHandle(questionTextAreaRefCb, () => questionTextAreaRef.current);

  const onAddBlank = () => {
    const { current: questionTextArea } = questionTextAreaRef;
    if (hasBlank) {
      throw Error("Cannot add blank to question that already has one");
    }
    if (questionTextArea == null) {
      throw Error("Question text area ref is null");
    }

    const { selectionStart, selectionEnd, value: question } = questionTextArea;
    let newQuestion = question.slice(0, selectionStart) + fitbBlank + question.slice(selectionEnd);
    setFieldValue("question", newQuestion);
    questionTextArea.setSelectionRange(
      selectionStart + fitbBlank.length,
      selectionStart + fitbBlank.length,
    );
    questionTextArea.focus();
  };

  // Update the global state with the current form values
  const { setValue, addItem } = useCourseEditorContext();
  const updateState = useCallback(() => {
    if (mode !== "edit") return;

    const setValueOptions = {
      condition: (_prev?: CourseData.FitbQuestion, parent?: CourseData.FitbStep) =>
        parent?.id === props.id,
    };

    setValue(
      path,
      {
        ...getValues(),
        options: getValues("options").map((answer) => answer.value),
      },
      setValueOptions,
    );
  }, [path, getValues, mode]);
  const debouncedUpdate = useMemo(() => _.debounce(updateState, 500), [updateState]);

  useEffect(() => {
    return updateState;
  }, []);
  watch(async () => debouncedUpdate());

  return (
    <>
      <CourseTabTitle className="mx-auto w-fit">{t("Question.FillBlank")}</CourseTabTitle>

      <CourseStepPaddingWrapper>
        <EditableBox
          className="relative"
          onFocus={() => setQuestionIsFocused(true)}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              setQuestionIsFocused(false);
            }
          }}
          ref={questionContainerRef}
        >
          <TransparentTextArea
            className="fs-20 pb-10"
            minRows={2}
            maxRows={4}
            ref={questionTextAreaRef}
            disabled={mode === "view"}
            {...registerQuestion}
          />
          {showAddBlank && (
            <Button
              disabled={mode === "view"}
              onClick={onAddBlank}
              variant="outlined"
              size="pill"
              className="absolute bottom-2.5 left-[50%] -translate-x-1/2"
            >
              {t("Action.AddBlank")}
            </Button>
          )}
        </EditableBox>
      </CourseStepPaddingWrapper>

      <CourseStepDivider />

      <CourseStepPaddingWrapper>
        <div className="flex flex-col gap-5">
          {answers.map((answer, i) => (
            <McAnswer
              key={i}
              index={i}
              text={answer.value}
              form={fitbQuestionForm}
              fieldPath={`options.${i}.value`}
              correctAnswerPath="correctAnswer"
              correct={i === correctAnswer}
              showDelete={answers.length > 3}
            />
          ))}
        </div>

        {answers.length < 3 && (
          <Button
            variant="outlined"
            size="pill"
            className="mt-5 self-start"
            onClick={() => addItem(path.concat("options"))}
            disabled={mode === "view"}
          >
            {t("Question.AddAnswer")}
          </Button>
        )}
      </CourseStepPaddingWrapper>
    </>
  );
}

export default FitbStep;
